/**
 * Removes auth tokens from URLs before they are sent to PostHog.
 * The sign-in callback uses the implicit flow, so tokens live in the hash.
 * The path and non-sensitive params (including error_code) are kept.
 */

const SENSITIVE_PARAM_NAMES = [
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'expires_at',
  'token_type',
] as const;

const SENSITIVE_PARAMS = new Set<string>(SENSITIVE_PARAM_NAMES);

const SKIP_PROPERTY_KEYS = new Set(['$snapshot_data']);

const MAX_PROPERTY_DEPTH = 6;

function paramName(part: string): string {
  const eq = part.indexOf('=');
  const raw = eq === -1 ? part : part.slice(0, eq);
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' ')).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

function stripSensitiveParts(query: string): {
  value: string;
  changed: boolean;
} {
  if (!query) {
    return { value: '', changed: false };
  }
  const parts = query.split('&');
  const kept = parts.filter(
    (part) => part.length > 0 && !SENSITIVE_PARAMS.has(paramName(part)),
  );
  const originalCount = parts.filter((part) => part.length > 0).length;
  return { value: kept.join('&'), changed: kept.length !== originalCount };
}

function containsSensitiveUrlParam(value: string): boolean {
  const lower = value.toLowerCase();
  return SENSITIVE_PARAM_NAMES.some((name) => {
    const token = `${name}=`;
    let index = lower.indexOf(token);
    while (index !== -1) {
      if (index === 0) {
        return true;
      }
      const previous = lower[index - 1];
      if (previous === '?' || previous === '&' || previous === '#') {
        return true;
      }
      index = lower.indexOf(token, index + token.length);
    }
    return false;
  });
}

/**
 * Drop sensitive query and hash params. A hash that only held tokens is
 * removed. Non-sensitive hash params, such as error_code, stay.
 */
export function sanitizeAnalyticsUrl(value: string): string {
  if (!value) {
    return value;
  }

  const hashIndex = value.indexOf('#');
  const beforeHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex + 1);

  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1);

  const strippedQuery = stripSensitiveParts(query);
  let strippedHash = hash;
  let hashChanged = false;
  if (hash.includes('=')) {
    const result = stripSensitiveParts(hash);
    strippedHash = result.value;
    hashChanged = result.changed;
  }

  if (!strippedQuery.changed && !hashChanged) {
    return value;
  }

  let next = path;
  if (strippedQuery.value) {
    next += `?${strippedQuery.value}`;
  }
  if (strippedHash) {
    next += `#${strippedHash}`;
  }
  return next;
}

function isUrlPropertyName(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    normalized.endsWith('url') ||
    normalized.endsWith('href') ||
    normalized.endsWith('referrer') ||
    normalized.endsWith('pathname') ||
    normalized === 'page_path' ||
    normalized === 'path' ||
    normalized === 'landing_page'
  );
}

function looksLikeUrl(value: string): boolean {
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/') ||
    value.includes('://')
  );
}

function shouldSanitizeProperty(key: string, value: string): boolean {
  if (isUrlPropertyName(key)) {
    return (
      value.includes('#') ||
      value.includes('?') ||
      containsSensitiveUrlParam(value)
    );
  }
  return looksLikeUrl(value) && containsSensitiveUrlParam(value);
}

function sanitizeArray(values: unknown[], depth: number): void {
  if (depth > MAX_PROPERTY_DEPTH) {
    return;
  }
  for (let index = 0; index < values.length; index += 1) {
    const item = values[index];
    if (typeof item === 'string') {
      if (looksLikeUrl(item) && containsSensitiveUrlParam(item)) {
        values[index] = sanitizeAnalyticsUrl(item);
      }
      continue;
    }
    if (Array.isArray(item)) {
      sanitizeArray(item, depth + 1);
      continue;
    }
    if (item && typeof item === 'object') {
      sanitizeRecord(item as Record<string, unknown>, depth + 1);
    }
  }
}

function sanitizeRecord(record: Record<string, unknown>, depth: number): void {
  if (depth > MAX_PROPERTY_DEPTH) {
    return;
  }
  for (const key of Object.keys(record)) {
    if (SKIP_PROPERTY_KEYS.has(key)) {
      continue;
    }
    const value = record[key];
    if (typeof value === 'string') {
      if (shouldSanitizeProperty(key, value)) {
        record[key] = sanitizeAnalyticsUrl(value);
      }
      continue;
    }
    if (Array.isArray(value)) {
      sanitizeArray(value, depth + 1);
      continue;
    }
    if (value && typeof value === 'object') {
      sanitizeRecord(value as Record<string, unknown>, depth + 1);
    }
  }
}

/** Sanitize URL fields on every PostHog event, including nested $set bags. */
export function sanitizePostHogEventUrls<T>(event: T): T {
  if (!event || typeof event !== 'object') {
    return event;
  }
  const record = event as Record<string, unknown>;
  for (const key of ['properties', '$set', '$set_once'] as const) {
    const value = record[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitizeRecord(value as Record<string, unknown>, 0);
    }
  }
  return event;
}

/** Session replay page URLs are passed as `name` to maskCapturedNetworkRequestFn. */
export function maskCapturedRecordingUrl<T extends { name?: string }>(
  request: T,
): T {
  if (!request || typeof request.name !== 'string') {
    return request;
  }
  const name = sanitizeAnalyticsUrl(request.name);
  if (name === request.name) {
    return request;
  }
  return { ...request, name };
}
